import { getAssetsPath } from "./utils/pathConfig";
import { SFX_SPRITE_MAP } from "./sfxSpriteMap"; // AUTO-GENERATED — run "npm run build:sfx" to regenerate

// Use centralized path configuration
const BASE_PATH = getAssetsPath();

// ============================================================================
// AUDIO CONFIGURATION
// ============================================================================
export const AUDIO_CONFIG = {
  // The ambient layer plays silently alongside its background track and fades
  // up when the player goes idle, while the background fades away under it.
  //
  // ON since 2026-09-17, when Mad Labs got its own. ONE track, not two: the
  // base game and the bonus room share it, so `feature_ambient_music` resolves
  // to the same file — see SOUND_NAME below. Zeus's pair is gone from disk.
  //
  // When false the track is never registered, and every ambient path in
  // SoundManager then finds nothing in HowlCache and no-ops: the crossfade, the
  // silent start, and the paired fade-out all guard on the Howl existing.
  AMBIENT_MUSIC_ENABLED: true,
};

// ============================================================================
// AUDIO SPRITE CONFIGURATION
// ============================================================================
// SFX_SPRITE_MAP imported above from auto-generated sfxSpriteMap.js

// ============================================================================
// CONDITIONAL SOUND LOADING - Build sounds array based on enabled features
// ============================================================================
const buildSoundsArray = () => {
  const sounds = [
    // ── Looping sounds — one entry per file in public/assets/sounds/looping/ ──
    // Everything else is the SFX sprite below. If a file is not in one of these
    // two places it does not exist for the game.
    { name: "background_music",         url: `${BASE_PATH}/sounds/looping/main_background_music.mp3`,    loop: true, volume: 1 },
    { name: "feature_background_music", url: `${BASE_PATH}/sounds/looping/feature_background_music.mp3`, loop: true, volume: 1 },
    // Hold & Win's own room track (2026-10-19 — it shared the free spins' one
    // before). Started by the wipe into H&W, from the base game or from free
    // spins; the wipe out hands back to whichever room it came from.
    { name: "hold_and_win_music",       url: `${BASE_PATH}/sounds/looping/hold_and_win_music.mp3`,       loop: true, volume: 1 },
    // Plays on loop while the intro popup is up; fades out fast when it closes.
    { name: "intro_music",              url: `${BASE_PATH}/sounds/looping/intro_music.mp3`,              loop: true, volume: 1 },
    // Fades in under the whole win celebration, replacing the background track.
    { name: "win_music",                url: `${BASE_PATH}/sounds/looping/win_music.mp3`,                loop: true, volume: 1 },
    // The TOTAL WIN popup's own loop (2026-10-22, the designer's) — free spins
    // and Hold & Win; MAX WIN keeps win_music (Games/Shared/outroWinMusic).
    { name: "total_win_music",          url: `${BASE_PATH}/sounds/looping/total_win_music.mp3`,          loop: true, volume: 1 },
    // The Royal jackpot's bed (2026-10-30): bars 9-12 of the big win track
    // (16-24 s, 120 BPM), an 8 s loop with a 50 ms seam crossfade — the same
    // music, a different part, so the Royal does not sound like a big win.
    { name: "royal_music",              url: `${BASE_PATH}/sounds/looping/royal_music.mp3`,              loop: true, volume: 1 },
    // ONE reel spin for the whole game. The bonus room deliberately plays this
    // same track — there is no feature_reel_spin; see SOUND_NAME below.
    { name: "reel_spin",                url: `${BASE_PATH}/sounds/looping/reel_spin.mp3`,                loop: true, volume: 1 },
    { name: "hold_and_win_zoom_spin",   url: `${BASE_PATH}/sounds/looping/hold_and_win_zoom_spin.mp3`,   loop: true, volume: 1 },
    // Hold & Win's own respin spin (2026-10-22) — it played reel_spin before.
    { name: "hold_and_win_reel_spin",   url: `${BASE_PATH}/sounds/looping/hold_and_win_reel_spin.mp3`,   loop: true, volume: 1 },
    // The strip inside the gold scatter's open doors. A reel of its own, so it
    // loops like one — and like one it must NOT go in the SFX sprite: play()
    // checks the sheet first, and a loop found there plays once and stops.
    // NOT LOADED (2026-10-15, 25 MB limit): nothing in Cleopatra's Fire plays it.
    // FILE REMOVED (2026-10-22, Mad Labs only).
    // { name: "scatter_spin",             url: `${BASE_PATH}/sounds/looping/scatter_spin.mp3`,             loop: true, volume: 1 },
    // NOT LOADED (2026-10-15, 25 MB limit): nothing in Cleopatra's Fire plays it.
    // FILE REMOVED (2026-10-22, Mad Labs only).
    // { name: "golden_mystery_spin",      url: `${BASE_PATH}/sounds/looping/golden_mystery_spin.mp3`,      loop: true, volume: 1 },
    // The electricity the scene runs over the REELS while something is being
    // decided — the scatter anticipation and the Golden Mystery's build-up. It
    // is held for as long as the clip is, so it loops like the spins do.
    // NOT LOADED (2026-10-15, 25 MB limit): nothing in Cleopatra's Fire plays it.
    // FILE REMOVED (2026-10-22, Mad Labs only).
    // { name: "reel_glow",                url: `${BASE_PATH}/sounds/looping/reel_glow.mp3`,                loop: true, volume: 1 },
    { name: "counter",                  url: `${BASE_PATH}/sounds/looping/counter.mp3`,                  loop: true, volume: 1 },
    // The fire AROUND the reels while an anticipation chain runs (2026-10-20) —
    // looped for as long as that frame is up (GameViewSimpleController).
    { name: "anticipation_fire",        url: `${BASE_PATH}/sounds/looping/anticipation_fire.mp3`,        loop: true, volume: 1 },
    // The drums under every anticipation's darkened room (2026-10-22) — looped
    // while the background is dark (GameViewSimpleController darken/lighten).
    { name: "anticipation_drums",       url: `${BASE_PATH}/sounds/looping/anticipation_drums.mp3`,       loop: true, volume: 1 },

    // ── SFX sprite — every one-shot, built by "npm run build:sfx" ──
    { name: "sfx_sprite",             url: `${BASE_PATH}/sounds/sfx-sprite.mp3`, sprite: SFX_SPRITE_MAP, volume: 1 },
  ];

  // ── Ambient layer — only when it exists ──
  if (AUDIO_CONFIG.AMBIENT_MUSIC_ENABLED) {
    sounds.push(
      { name: "ambient_music",         url: `${BASE_PATH}/sounds/looping/ambient_music.mp3`,         loop: true, volume: 1 },
    );
  }

  return sounds;
};

export const GAME_SOUNDS = {
  sounds: buildSoundsArray(),
};

// ============================================================================
// SOUND NAME MAPPINGS
// All sound names registered here. If a sound is disabled in the sprite
// manifest or not loaded as an individual Howl, soundManager.play() silently
// does nothing — no conditional logic needed.
// ============================================================================
// ============================================================================
// FADE TIMING
// ============================================================================
// ONE value for every music fade in the game — the win celebration's handover,
// the base <-> bonus swap, the intro popup, the outro dialogs.
//
// Deliberately SHORT. This game is quick and abrupt, and the old 500ms read as
// the outgoing track hanging on after the thing that ended it. Long enough to
// avoid a click, no longer. Raise it here and every fade follows.
export const MUSIC_FADE_MS = 150;

// A one-shot cut short — a banner ended by a skip, or by the next tier
// starting. Shorter still: it must not delay whatever replaces it.
export const SFX_CUT_FADE_MS = 50;

// ── Mad Labs-only files REMOVED (2026-10-22) ── gamble_*, scatter_door /
// scatter_spin / scatter_win / scatter_lose (the gamble scatter's reveal),
// golden_mystery_* / collect_* / multiplier_appear / upgrade, mystery_* /
// door_opening, level_progress_*, reel_glow, tap_to_continue, and the
// additional_free_spins_* trio (the retrigger plays the intro's sound), and
// free_spin_intro_close (no intro closes on its own sound). Their names stay
// below so the Mad Labs call sites (never reached in this game) still compile;
// play() on a name with no file is a no-op.
export const SOUND_NAME = {
  // ── Looping (individual Howls, from looping/) ──
  background_music: "background_music",
  ambient_music: "ambient_music",
  feature_background_music: "feature_background_music",
  hold_and_win_music: "hold_and_win_music",
  // ── The SAME track as `ambient_music`, deliberately ──
  //
  // Mad Labs has one ambient bed and it serves both rooms. The name is kept
  // rather than deleted because it is what the pairing reads: SoundManager's
  // `_getAmbientFor` maps feature_background_music -> this, and the free-spin
  // popups pick between the two by room. Pointing it at the one registered
  // track keeps every one of those call sites correct with no change, and a
  // second file can be dropped in here later without touching any of them.
  //
  // SOUND_VOLUMES is keyed by the VALUE, not the key, so the single
  // `ambient_music` entry there covers both names. A second entry under this
  // name would never be read.
  feature_ambient_music: "ambient_music",
  intro_music: "intro_music",
  win_music: "win_music",
  total_win_music: "total_win_music",
  royal_music: "royal_music",
  reel_spin: "reel_spin",
  // The H&W reels moving back before the End Collector (2026-10-29): its own
  // copy of reel_spin, louder over the new H&W track.
  hold_and_win_zoom_spin: "hold_and_win_zoom_spin",
  // Hold & Win's respins (2026-10-22): their own loop while the cells spin,
  // and their own stop for each cell that lands.
  hold_and_win_reel_spin: "hold_and_win_reel_spin",
  hold_and_win_reel_stop: "hold_and_win_reel_stop",
  // A coin landing in a Hold & Win cell (2026-10-22): the SAME fireball as
  // coin_land, its own copy so it can sit lower there (under the chests and the
  // scarab) while the base game's coin_land is level with scatter and wild.
  hold_and_win_coin_land: "hold_and_win_coin_land",
  // The respins counter going DOWN (3 -> 2 -> 1 -> 0) — Zeus's own
  // respins_counter (2026-10-22; Griffin's add_respin was "not good"),
  // HoldAndWinCounterController's tick.
  respins_counter: "respins_counter",
  // NO feature_reel_spin. The free-spins reels play `reel_spin`, the same
  // track the base game does, so the bonus room needs no second one. The name
  // survives only at Zeus feature call sites (Pot Collection, Hold and Win,
  // Royal Collect), where it is now one more dead name that no-ops — the same
  // end state as every other Zeus sound on this branch.

  // The strip spinning behind the gold scatter's open doors. Starts with the
  // strip and stops when it settles, exactly as reel_spin does for a reel.
  scatter_spin: "scatter_spin",
  // The Golden Mystery's track, same contract again — but SHARED. A row of
  // cells spins at once, so it is reference counted: it starts with the first
  // track and stops when the last coin lands. See loopSoundRefs.
  golden_mystery_spin: "golden_mystery_spin",
  // The reel electricity. Not a call site's to start: it belongs to the ACCENT
  // that draws it, so sceneAccents plays and stops it with the clip — one
  // owner for both rooms. See ACCENT_LOOP_SOUND there.
  reel_glow: "reel_glow",
  counter: "counter",

  // ── One-shots (via the sprite, from one-shot/) ──
  basic_win: "basic_win",
  // The TRIGGER wins (2026-10-28, the designer's): a round with a scatter
  // line plays win_scatter ("win 2 v2" — 3 scatters, and the free spins' 2/3
  // scatter retriggers), one with the Hold & Win coin line win_coin ("win 3")
  // — instead of basic_win (goldenWinSound).
  // win_: "win_",
  win_scatter: "win_scatter",
  win_coin: "win_coin",
  button_click: "button_click",
  spin: "spin",
  reel_stop: "reel_stop",
  // Turbo and slam stops (2026-10-20) — reel_stop used to play that role.
  quick_stop: "quick_stop",
  // The fire AROUND the reels (the board frame) while anticipating — a LOOP
  // (2026-10-20), GameViewSimpleController's reel anticipation.
  anticipation_fire: "anticipation_fire",
  // The drums WITH it, while the room is darkened — a LOOP (2026-10-22).
  anticipation_drums: "anticipation_drums",
  // Its intro (once, then the loop above takes over) and its END HIT
  // ("du-du DUM") — both cut from the designer's drums (2026-10-22).
  // anticipation_drums_in: "anticipation_drums_in",
  // anticipation_end: "anticipation_end",
  // (Both REVERTED 2026-10-22 — the designer's original drums are back; the
  // sound designer is to deliver a loop and an end hit.)
  // ...delivered (2026-10-22, anticipation_drum_end) — in the SFX sprite.
  anticipation_end: "anticipation_end",
  // The "TAP TO CONTINUE" / "TAP TO START" acknowledgement. Every screen that
  // waits for the player to move on uses THIS, not button_click.
  // tap_to_continue: "tap_to_continue",
  // ── This game's CLICK (2026-10-20) ── the old file was Mad Labs'. The name
  // stays for every caller; it resolves to button_click, and the volume map is
  // keyed by the value, so it plays at button_click's level. The old file is
  // out of the sprite (sprite-manifest.json).
  tap_to_continue: "button_click",
  // The win celebration's own closing flourish. It REPLACES tap_to_continue
  // inside the celebration — that screen plays this and nothing else — and it
  // is fired from playClosingAnimation, which is the single gate both the tap
  // and the auto-close timer pass through. Every other "tap to continue"
  // screen keeps tap_to_continue.
  // One per line as the win cycle steps through them — see useWinController.
  win_line: "win_line",
  win_animation_end: "win_animation_end",
  // The same shot for the TOTAL WIN close (2026-10-22), its own copy in the
  // sprite so it can sit lower than the big win's.
  total_win_end: "total_win_end",
  // The End Collector's FINAL hit (2026-10-22, the designer's collector_last;
  // a Chinese gong since 2026-10-29)
  // — with collector_final_hit, after the last coin is in (endCollector.js).
  collector_last: "collector_last",
  // ...and its REGULAR hit, every value arriving (the designer's collector).
  collector_hit: "collector_hit",
  // The gold/blue bolt thrown by LevelChargingAnimator — the free-spins level
  // pips and the Golden Mystery's multiplier volley. NOT the lightning strike;
  // that is lightning_fly, a different effect with no file of its own.
  trail_sound: "trail_sound",
  // EVERY Hold & Win trail — the chest collections and the End Collector —
  // sounds different from the base game's (2026-10-22; the scarab's shots are
  // silent, it has scarab_activate): the
  // fireball flight 4 semitones down, top rolled off (lowpass 4 kHz), +3 dB at
  // 120 Hz — deeper and heavier. The base game's logo trails keep trail_sound.
  // (2026-10-28: the file is trail_sound's own again — the deeper copy is
  // gone; only the level differs, see the volumes.)
  hold_and_win_trail: "hold_and_win_trail",
  // The Golden Mystery's upgrade burst — the flash a coin takes when a
  // multiplier or a level raises its value. Fired per VOLLEY, not per coin;
  // see goldenMysteryRunner.
  upgrade: "upgrade",
  // A coin arriving in a Golden Mystery cell — the cell's reel_stop. Resolved
  // through goldenLandingSound, which still names `collector_in` and
  // `coin_multiplier` as STUBS: no file on disk, and play() no-ops on a name
  // it does not know.
  coin_land: "coin_land",
  // The COLLECTOR arriving in a Golden Mystery cell — the magnet. Same
  // resolver and same per-frame gate as coin_land; see goldenLandingSound,
  // where `coin_multiplier` is now the only landing left without a file.
  collect_land: "collect_land",
  // A MULTIPLIER arriving in a Golden Mystery cell. The last of the three
  // golden landings to get a file — goldenLandingSound now names no stubs.
  multiplier_appear: "multiplier_appear",
  // The magnet WORKING — fired on `cl.activation` starting, the same cue the
  // lightning volley hangs off, so the strikes and the sound leave together.
  // Not the landing: that is collect_land, a beat earlier.
  collect_activate: "collect_activate",
  // The Golden Mystery's payout. It stands in place of basic_win on a round
  // the feature paid, exactly as scatter_win stands in place of it on a
  // trigger — and it is gated the same way, so a win big enough to earn a
  // banner plays the banner instead. See goldenWinSound.
  golden_mystery_win: "golden_mystery_win",
  // ONE sound for every scatter that arrives, however it arrives: the normal
  // `sc`, the gold gamble `scg`, and the +FS `bs`. They share symbolPay
  // "scatter", which is what every landing resolver keys on. It replaces
  // reel_stop on a reel that carries one — the announcement, not both.
  scatter_land: "scatter_land",
  // The wild's own landing (2026-10-19) — picked by the reel like scatter_land.
  wild_land: "wild_land",
  // The scatter that did NOT come good. Registered 2026-09-17 and not yet
  // fired from anywhere — Vladimir is to say where it belongs.
  scatter_lose: "scatter_lose",
  // The gold scatter's own moment, AFTER it has landed: its doors slide open
  // and a strip of symbols spins inside the window. Declared by the reveal
  // config that owns those doors, so a lid that is not this symbol stays quiet.
  scatter_door: "scatter_door",
  // The trigger itself: three scatters, the strip landed on a package that
  // PAID, and the whole line is about to celebrate. It replaces the basic_win
  // chime that used to stand in for this — see useWinController.
  scatter_win: "scatter_win",
  // The free-spins intro dialog, as it appears. The play and stop calls in
  // FreeSpinIntroDialogController were already written against this name and
  // had simply been firing into nothing — there was no file until now.
  free_spin_intro: "free_spin_intro",
  // The full-screen WIPE between the base game and the bonus room, both ways.
  // Fired from TransitionDialogController.startSequence, which is the one
  // entry point and is guarded to run once per wipe.
  transition: "transition",
  // The same popup LEAVING. One sound for both ways out — the player's tap and
  // the 10s auto-hide run the same close path, deliberately.
  free_spin_intro_close: "free_spin_intro_close",
  // The free spins' closing announcement, fired from the TOTAL WIN dialog's
  // startSequence — the same beat that ducks the feature track. The dialog
  // also names `total_win_loop`, which has no file: that one is a LOOP and
  // would have to be registered in buildSoundsArray, never in the sprite.
  total_win: "total_win",
  // The free spins' other two endings. Same beat as total_win — fired from
  // each dialog's startSequence, right after the feature track is ducked.
  // MaxWinDialog (the Hold & Win one) is not mounted in this game; the
  // free-spins pair is the whole set.
  max_win: "max_win",
  no_win: "no_win",
  // The Royal jackpot (2026-10-22) — played max_win as a stand-in before.
  royal_win: "royal_win",

  // ── Cleopatra's Fire board sounds (batch 3, 2026-10-22) ──
  // A jackpot coin landing plays its tier's sound instead of coin_land (base
  // reels and Hold & Win cells). Keyed by the jackpot name (JACKPOT_NAME_TO_SKIN).
  mini_coin: "mini_coin",
  minor_coin: "minor_coin",
  midi_coin: "midi_coin",
  major_coin: "major_coin",
  grand_coin: "grand_coin",
  // Their Hold & Win copies (2026-10-29) — the H&W cells play these, louder
  // over the new H&W track; the base and free-spins reels keep the originals.
  hold_and_win_mini_coin: "hold_and_win_mini_coin",
  hold_and_win_minor_coin: "hold_and_win_minor_coin",
  hold_and_win_midi_coin: "hold_and_win_midi_coin",
  hold_and_win_major_coin: "hold_and_win_major_coin",
  hold_and_win_grand_coin: "hold_and_win_grand_coin",
  // The chests landing in a Hold & Win cell, and each value dropping into one
  // as it collects (chestCollection's arrive).
  silver_chest_land: "silver_chest_land",
  gold_chest_land: "gold_chest_land",
  silver_chest_collect: "silver_chest_collect",
  gold_chest_collect: "gold_chest_collect",
  // The scarab landing, and the scarab working (its ×N reveal at settlement).
  scarab_land: "scarab_land",
  scarab_activate: "scarab_activate",
  // A locked row's plate opening and going (HoldAndWinGameViewController.unlockRows).
  unlock: "unlock",

  // ── The Additional Free Spins popup, three beats of one performance ──
  // in -> the box closing and the count appearing -> out.
  additional_free_spins_intro: "additional_free_spins_intro",
  additional_free_spins_win: "additional_free_spins_win",
  additional_free_spins_outro: "additional_free_spins_outro",

  // ── The bonus scene's level boxes ──
  // A tear lighting as a scatter's trail reaches it, and the box converting
  // once it is full. Both fire from LevelProgressController, so every caller
  // gets them — the trails, the tier walk and the entry-box flourish alike.
  level_progress_add: "level_progress_add",
  level_progress_close: "level_progress_close",

  // ── The gamble machine ──
  // in, the strip spinning, the rat, the won package, and out.
  gamble_intro: "gamble_intro",
  gamble_spin: "gamble_spin",
  gamble_loss: "gamble_loss",
  gamble_win: "gamble_win",
  gamble_outro: "gamble_outro",
  // A mystery lid arriving on a reel. Resolved the same way the scatter is —
  // off symbolPay, in the reel-stop sound resolvers.
  mystery_land: "mystery_land",
  // The mystery's DOORS parting — the beat the build-up was for. Declared by
  // the reveal configs (`openSound`), which is how one class can run every
  // reveal in the game and only the ones that are doors make a noise. Both
  // kinds of mystery use it: the ordinary lid, and the Golden cell's lid,
  // which is the same rig.
  door_opening: "door_opening",
  // The mystery's BUILD-UP, one per authored clip. The tier picks the clip and
  // the clip picks the sound, so the two can never disagree — see
  // MYSTERY_DRAMA_SOUNDS in Games/Shared/mysteryDrama.
  mystery_reveal_fast: "mystery_reveal_fast", // goes with `anticipation`
  mystery_reveal_slow: "mystery_reveal_slow", // goes with `anticipation_strong`
  // The GOLDEN reveal — the gold sign burning away and the window opening onto
  // the track. It is the payoff of the build-up above, not a second build-up,
  // and it belongs to B2 alone: an ordinary mystery never reaches this clip.
  // Fired at step 6 of GoldenMysteryCellController.play, on `golden.reveal`.
  golden_mystery_reveal: "golden_mystery_reveal",

  // Win celebration — ONE sound per level. The KEY must equal the win-type
  // string in PROVIDER_CONFIG.WIN_TYPES, because playWinPhaseSound looks it up
  // by that string; the VALUE is the file name. They differ for the top tier:
  // the win type is "supermega_win", the file is "super_mega_win.mp3".
  big_win: "big_win",
  mega_win: "mega_win",
  supermega_win: "super_mega_win",
};


// ============================================================================
// PER-SOUND VOLUME MAP
// Default volume for each sound (0.0 – 1.0). soundManager.play() applies this
// automatically. Adjust individual values to balance the mix without touching
// every call site. Sounds not listed here default to 1.0.
// ============================================================================
export const SOUND_VOLUMES = {
  // ── Music ── ONE level in dB, solved not chosen
  //
  // gain = 10^((target - fileLUFS)/20) against a shared target of -17.651 dB,
  // so all three play at the same loudness to within 0.001 dB. A flat 0.5
  // looked like "one level" and was not: the bonus room's track is mastered
  // 1.2 dB hotter than the base game's, so walking into the room was a step up
  // in volume, and the win music sat between the two.
  //
  // These three are NEVER heard together — one replaces another across a wipe
  // or a crossfade — which is exactly why they have to match. A difference
  // between tracks that swap reads as the game getting louder, not as two
  // pieces of music.
  //
  // Taken down 1.5 dB from -17.651, and all three MOVED TOGETHER. Lowering
  // only the two background tracks would have re-opened the gap that levelling
  // them closed: win_music does not play beside them, it REPLACES them for the
  // length of a celebration and hands back. A track that swaps in louder than
  // the one it replaced is the same fault in the other direction.
  //
  // Re-solve, do not nudge, if any of these files is replaced.
  // background_music: 0.421,
  // feature_background_music: 0.367,
  // ── New tracks (2026-10-19), solved onto the SAME line ── the old pair
  // played at -19.564 / -19.567 dB (-12.05 LUFS x 0.421, -10.86 x 0.367), so
  // the new files keep that level: base -13.23 LUFS, free spins -10.24,
  // Hold & Win -15.53 (measured on the 96 kbps files in the game).
  background_music: 0.482,
  // feature_background_music: 0.342,
  // The designer's "free spin v2" (2026-10-28): -13.32 LUFS against the old
  // -10.24, kept at the old played level (-19.56 dB; its loud parts within
  // 0.2 dB of the old track's too).
  feature_background_music: 0.488,
  // hold_and_win_music: 0.628,
  // The designer's "bonus v2" (2026-10-28): -11.76 LUFS against the old
  // -15.53, kept at the old played level (-19.57 dB, the music line).
  // hold_and_win_music: 0.407,
  // "A tiny bit louder … match base game volume, realistically" (2026-10-28):
  // +1.5 dB. The base track swells (LRA 4.9 — its loud parts -17.3 dB played)
  // and this one is flat (LRA 1.6), so equal on the average it read quieter;
  // now its level sits on the base track's swells.
  hold_and_win_music: 0.484,
  // win_music: 0.417,
  // Batch 3 (2026-10-22): big_wins_music_loop, -12.06 LUFS (96 kbps) against -11.99, kept at the old played level.
  // win_music: 0.42,
  // big_win_music_loop (2026-10-22, the designer's): -11.14 LUFS (96 kbps),
  // solved to the old played level (-19.59 dB): 0.42 x 10^((-12.06 + 11.14)/20).
  // win_music: 0.378,
  // The designer's "bigwins v2" (2026-10-28): -11.27 LUFS (96 kbps) against
  // -11.14, kept at the old played level (-19.55 dB).
  // win_music: 0.384,
  // ...its corrected re-delivery, same name (2026-10-28): -10.74 LUFS, at the
  // same played level.
  win_music: 0.361,
  // TRIAL (2026-10-28, "from 16th second it starts having some interesting
  // melody … make a loop out of it"): the file is now bars 9-12 of it only —
  // 16.0-24.0 s at 120 BPM, the best bar-aligned seam from there; the wrap
  // crossfades 50 ms. 8 s, -10.61 LUFS, at the same played level. The full
  // 32 s file at 0.361 is the revert.
  // win_music: 0.356,
  // (REVERTED 2026-10-29: the full 32 s "bigwins v2" is back, at 0.361.)
  // total_win_music_loop (2026-10-22): -11.29 LUFS (96 kbps), at win_music's
  // played level (-19.59 dB): 10^((-19.59 + 11.29)/20).
  // total_win_music: 0.385,
  // total_win_music: 0.544, // louder (2026-10-22: "volume … is low"): +3 dB
  // The designer's "total win v2" (2026-10-28): -10.87 LUFS (96 kbps) against
  // -11.29, kept at the old played level (-16.5 dB, the +3 dB included).
  total_win_music: 0.518,
  // royal_music (2026-10-30): -11.04 LUFS (win_music -10.74) -> 0.374 is
  // win_music's played level; +3 dB over it — the Royal plays it ALONE (no
  // level banners on top), and it read "so quiet".
  // royal_music: 0.528,
  royal_music: 0.746, // +3 dB more ("amplify more", 2026-10-30)
  // ── The ambient, levelled onto INTRO MUSIC ──
  //
  // -12.87 LUFS measured. intro_music is -10.84 LUFS at 0.15, so it plays at
  // -27.318 dB, and matching that is
  //
  //   10^((-27.318 + 12.87)/20) = 0.189  ->  plays at -27.341 dB
  //
  // 0.023 dB off intro_music, and 8.17 dB under the three background tracks.
  //
  // ── It was levelled WITH the music first, and that was wrong ──
  //
  // Solved onto the music line it came out at 0.485, then 0.339 for "30%
  // quieter". Both were still too present. Vladimir, 2026-09-17: as quiet as
  // the intro.
  //
  // The reasoning that put it on the music line does not survive contact with
  // what the track is FOR. win_music replaces the background because something
  // is happening, so it must not change the volume of the game. The ambient
  // replaces it because nothing is happening — the player has walked away. It
  // is a room tone, not a piece of music, and a room tone that arrives at
  // listening level is just the music again.
  //
  // Still solved, not chosen: it is pinned to intro_music's PLAYED level, so if
  // either file is replaced, re-measure both and re-solve this one. Do not
  // nudge 0.189.
  //
  // One entry for both names: `feature_ambient_music` resolves to
  // "ambient_music", and this map is keyed by the value.
  // ambient_music: 0.189,
  // ── The INTRO track is the ambient now (2026-10-20) ── ambient_music.mp3
  // is a copy of intro_music.mp3, the bed every room (base, free spins, H&W)
  // fades to after the idle wait. Same file, same level as the intro: 0.152.
  ambient_music: 0.152,
  // intro_music: 0.15,
  // New file (2026-10-19): -11.38 LUFS, solved onto the old one's PLAYED level
  // (-11.27 LUFS x 0.15 = -27.748 dB) — so ambient_music, pinned to that
  // level, stays as it is.
  intro_music: 0.152,

  // ── Win celebration ── the counter far below; win_music is levelled with
  // the other two tracks above, because it REPLACES them rather than joining
  // them.

  // ── The three banners are ONE level in dB, solved not chosen ──
  //
  // gain = 10^((target - fileLUFS)/20) against a shared target of -11.127 dB,
  // so all three play at the same loudness to within 0.002 dB. The files are
  // mastered a full dB apart (-6.7 / -7.1 / -7.7), and at a flat 0.6 that made
  // SUPER MEGA the quietest of the three — the top of the chain arriving
  // smaller than the bottom of it.
  //
  // Only one is ever on screen: each tier's clip stops the previous one, so
  // they are heard in SUCCESSION, and any step between them reads as the
  // celebration losing rather than gaining.
  //
  // The target is big_win where it already was, so nothing was turned down.
  //
  // Re-solve, do not nudge, if any of these three files is replaced.
  // big_win: 0.6,
  // mega_win: 0.626,
  // Batch 3 (2026-10-22), each kept at its old played level: big -8.90 LUFS
  // against -6.69, mega -9.46 against -7.06, super mega -9.34 against -7.70.
  big_win: 0.774,
  mega_win: 0.825,
  // ── Keyed by the VALUE, not the key ──
  //
  // `_getSoundVolume` is handed whatever `play()` was given, and the win
  // celebration calls `play(SOUND_NAME[winType])` — so what arrives here is
  // "super_mega_win", the FILE name. `supermega_win` is the win-TYPE string
  // and is the right key in SOUND_NAME; it is the wrong one here.
  //
  // Under the old name this entry was never read: the lookup missed, fell
  // through to the 1.0 default, and the top banner played 3.4 dB ABOVE the two
  // below it. The tier chain got quieter as it climbed, and it had done so
  // since the game was built — the levelling pass that set 0.674 was writing
  // into a key nothing looked at.
  //
  // It is the one entry in this table whose name differs from its SOUND_NAME
  // key, for the same reason that key exists. See the note there.
  // super_mega_win: 0.674,
  super_mega_win: 0.814,
  // Batch 3 (2026-10-22): a new tick, -13.9 LUFS momentary (96 kbps) — the old
  // one's exact level, so the number stays.
  // counter: 0.1,
  // The designer's tick (2026-10-22): measured looped (decoded), -22.63 LUFS
  // against the old -6.47, kept at the old played level: 0.1 x 10^(16.16 / 20).
  counter: 0.642,

  // ── Reels ──
  // ── THE THREE SPIN BEDS ARE ONE LEVEL, 2026-09-22 ──
  //
  // Vladimir: "make sure that gamble spin sound, and golden scatter spin sound
  // are same level as that golden mystery." They are the same gesture — a strip
  // of symbols turning while the player waits — so they are solved onto one
  // number rather than each against its own neighbours:
  //
  //   golden_mystery_spin  -9.1 LUFS x 0.282  = -20.10 dB   the reference
  //   scatter_spin         -7.0 LUFS x 0.221  = -20.10 dB
  //   gamble_spin         -10.8 LUFS x 0.343  = -20.10 dB
  //
  // This one came DOWN 2.64 dB from 0.3. The old note read "set above reel_spin
  // because it never competes with it: the board has stopped and this strip is
  // the only thing moving" — true, and it is why it drifted up. Matching its
  // two siblings is the stronger rule: the player hears these three as the same
  // thing, and a step between them reads as one feature being louder than
  // another for no reason.
  scatter_spin: 0.221,
  // It was levelled with scatter_spin at 0.25 and that was wrong for this
  // feature: the scatter's strip is the only thing moving, while this one runs
  // UNDER ten cells landing. A bed, not an event — so it sits about 4 dB below
  // coin_land and further below the collector, and the landings read on top.
  //
  // ── THE FILE CHANGED, 2026-09-22, and the number had to follow ──
  //
  // The 0.18 was solved against a -5.2 LUFS file. The replacement measures
  // -9.1 LUFS, so at the same gain it played 3.9 dB below where it was tuned —
  // landing on reel_spin's own -23.97 dB instead of the -20.09 dB it was given.
  //
  // Put back to -20.09 dB, which is 0.282. Vladimir: "increase the volume of
  // the golden mystery spin, its barely audible, it should be as loud as the
  // reel spin sound."
  //
  // ── Level with reel_spin means LOUDER than reel_spin ──
  //
  // On the meter the two were already equal, which is exactly why it read as
  // too quiet: reel_spin has the board to itself, while this runs UNDER
  // twenty-four cells landing, their bounces and the collector. Equal on a
  // meter is buried in the mix. So it sits about 4 dB above it, which is the
  // number this feature was tuned at before the file moved.
  golden_mystery_spin: 0.282,
  // -8.0 LUFS, 2.6s, and it LOOPS for the whole spin — which is why its number
  // is the lowest in the game and still reads as loud. A meter judges a file;
  // a loop is judged by how long the player sits inside it.
  //
  // Taken down 2 dB from 0.2 (-22.02 dB) to -24.02. Solved rather than nudged,
  // so the next change can be stated in dB too.
  //
  // ONE track for both rooms: free spins deliberately has no feature_reel_spin
  // — it plays this. So this number is the base game AND the bonus.
  // reel_spin: 0.159,
  // New file (2026-10-19): -8.78 LUFS against the old -8.50, kept at the old
  // one's played level (-8.50 LUFS x 0.159 = -24.47 dB).
  reel_spin: 0.164,
  // Its H&W zoom-out copy: +5 dB (H&W music review, 2026-10-29).
  hold_and_win_zoom_spin: 0.292,
  // hold_and_win_reel_spin — NEW (2026-10-22): -13.88 LUFS (96 kbps), on
  // reel_spin's played level (-24.48 dB): the same job in Hold & Win.
  // ── Hold & Win effects up with the new music (2026-10-29: "barely audible
  // because of our background … adapt other sounds") ── "bonus v2" is +6 dB
  // in the 0.5-6 kHz band where effects live (old vs new as played, A-weighted
  // per band) and never dips. Each effect gets back the margin it had over
  // the old track — its own A-weighted band mix x the music's change, rounded
  // to 0.5 dB. Shared sounds play H&W copies (hold_and_win_zoom_spin,
  // hold_and_win_<tier>_coin) so the base game and free spins are untouched.
  // hold_and_win_reel_spin: 0.295,
  hold_and_win_reel_spin: 0.589, // +6.0 dB (H&W music review, 2026-10-29)
  // hold_and_win_reel_stop — NEW (2026-10-22): -12.0 LUFS momentary, on
  // reel_stop's played level (-24.30 dB momentary).
  // hold_and_win_reel_stop: 0.243,
  hold_and_win_reel_stop: 0.385, // +4.0 dB (H&W music review, 2026-10-29)
  // respins_counter — Griffin's add_respin (2026-10-22): -33.1 LUFS momentary,
  // +10 dB baked into the file (-23.3); in Griffin it sat 1.4 dB under its
  // reel stop, so here 1.4 dB under ours (-24.30): -25.70 dB.
  // respins_counter: 0.759,
  // Zeus's respins_counter instead (2026-10-22, 3-orbs-of-zeus develop): it
  // played level with Zeus's reel spin (-31.19 vs -31.09 dB), so here level
  // with the Hold & Win spin (-24.48 dB). -29.78 LUFS, +6 dB baked (-23.32,
  // -1.2 dBTP).
  // respins_counter: 0.865,
  // respins_counter: 0.728, // a tiny bit quieter (2026-10-22): -1.5 dB
  // respins_counter: 0.613, // a bit less loud again (2026-10-22): -1.5 dB
  // respins_counter: 0.516, // a little more down (2026-10-22): -1.5 dB
  respins_counter: 1.0, // +6.0 (capped at 1, 0.25 short) dB (H&W music review, 2026-10-29)
  // reel_stop: 0.2,
  // New file (2026-10-19), kept at the old one's level. Too short for an
  // integrated reading (-inf), so both measured by their loudest 400 ms:
  // old -10.3, new -16.3 LUFS momentary.
  // reel_stop: 0.399,
  // Batch 2 (2026-10-20): -8.6 LUFS momentary against -16.3, kept at the old played level.
  reel_stop: 0.164,
  // quick_stop — NEW (2026-10-20): -9.4 LUFS momentary, solved onto
  // reel_stop's played level (-24.28 dB momentary): the same job, faster.
  quick_stop: 0.18,
  // anticipation_fire — NEW (2026-10-20): -10.85 LUFS, on the old spin-bed
  // level (-20.10 dB, Mad Labs): above the reel spin, under the landings.
  // anticipation_fire: 0.345,
  // Under the drums (2026-10-22: "drums should be more audible than fire"):
  // 6 dB under their played level (-17.10 dB) — the loop is -10.61 LUFS.
  // anticipation_fire: 0.237,
  // fire_loop (2026-10-22, the designer's loop, 2.04 s): -20.22 LUFS, on the
  // old file's played level: 0.237 x 10^((-10.61 + 20.22) / 20).
  // anticipation_fire: 0.717,
  // anticipation_fire: 0.903, // a bit more (2026-10-22): +2 dB
  // "not loud enough … a bit" (2026-10-22): +2 dB more. 0.903 left no room,
  // so +6 dB is baked into the file (-20.22 -> -14.66 LUFS, peak -6.4 dBTP):
  // 0.903 x 10^((2 - 5.56) / 20).
  anticipation_fire: 0.6,
  // anticipation_fire: 0, // OFF for now (2026-10-22): he is listening to the drums alone — back to 0.345
  // ...the file a real LOOP now (2026-10-22): the designer's had a 0.4 s
  // fade-in and a 0.8 s fade-out; cut to its steady part (0.50 s + 1.80 s,
  // both ends at the same level, 120 ms crossfade) — same fire, same level.
  // anticipation_drums — NEW (2026-10-22): -8.56 LUFS, on anticipation_fire's
  // played level (-20.09 dB): 0.345 x 10^((-10.85 + 8.56) / 20).
  // anticipation_drums: 0.265,
  // "barely hear drums … pronounce drums" (2026-10-22): +3 dB, over a room
  // track ducked 12 dB under them (GameViewSimpleController ANTICIPATION_DRUMS).
  // (2026-10-22: a loop cut from it was tried and reverted — the file is a
  // 3.5 s build of irregular hits, not a groove; it cannot loop naturally.
  // The designer is to deliver a real loop and an end hit.)
  // ...then built right (2026-10-22): its intro (0-0.984 s) once, then its
  // drum cycle — the hits at 1.004 s repeat at 2.192 s — 33 times, seams in
  // the quiet before a hit: 40 s, no fade anywhere. Stopped by a volume fade
  // (ANTICIPATION_DRUMS.DRUMS_FADE_MS). Same drums, same level.
  // ...and made SMALL (2026-10-22: 40 s was 644 KB): the file is ONE drum
  // cycle (1.188 s, 20 KB) looped by the code; the intro is its own one-shot.
  // (REVERTED 2026-10-22: the designer's original file is back.)
  // anticipation_drums: 0.374,
  // anticipation_drums: 0.471, // louder (2026-10-22): +2 dB
  // anticipation_drum_loop (2026-10-22, the designer's real loop, 2.04 s):
  // -9.50 LUFS, on the old file's played level: 0.471 x 10^((-8.56 + 9.50) / 20).
  // anticipation_drums: 0.525,
  anticipation_drums: 0.661, // a bit more (2026-10-22): +2 dB
  // anticipation_drum_end (2026-10-22, the designer's end shot): loudest
  // moment -11.6 in the mono sprite (-8.6 dB played on both channels), set
  // 3 dB over the drums' loudest (-7.6 x 0.525 = -13.2 dB): 10^(-1.6 / 20).
  // anticipation_end: 0.83,
  // "it has to be dramatic" (2026-10-22): +3.2 dB — the source mixed to mono
  // +5 dB under a -1 dBTP limiter (sprite -11.6 -> -10.0; more only squashes
  // the hit), and full volume.
  anticipation_end: 1,
  // anticipation_drums_in: 0.374,
  // // The end hit: its DUM 3 dB above the drums' loudest moment (-12.24 dB
  // // momentary; the drums play at -15.24), so it reads as the finish.
  // anticipation_end: 0.689,
  // -5.2 LUFS, and it is the hottest file in the looping folder.
  //
  // ── 3 dB under the spin beds ──
  //
  // It plays WITH them, never instead of them: under the reels during a scatter
  // anticipation, and under the Golden Mystery's own track during the build-up.
  // So it is solved as an interval rather than on its own —
  //
  //   golden_mystery_spin  -20.10 dB
  //   reel_glow            -23.10 dB    3 dB under it, which is 0.127
  //   reel_spin            -23.97 dB
  //
  // Vladimir, 2026-09-22: "somewhat a bit less loud than golden mystery spin
  // because they work at same time but still i need to be able to hear it."
  //
  // It was first placed 8 dB under, at 0.072, and that was too far: the two
  // that matter were 4 dB lower then, and taking the gap from the OLD reference
  // buried it. 3 dB is the step that reads as "quieter" without reading as
  // "absent" — and it lands level with reel_spin, which is right for the thing
  // announcing that the reels are worth watching.
  //
  // Stated in dB so the next move can be too: x1.26 per +2 dB, x0.79 per -2 dB.
  reel_glow: 0.127,

  // ── Effects ──
  // -7.5 LUFS, and it clips. SOLVED onto the same -16.816 dB as the landings:
  // a trail is what CARRIES a value to a cell and the landing is what receives
  // it, so the pair is one gesture and should not step.
  //
  // It was held under them at 0.3 (-17.97 dB) on the argument that it fires
  // many times a round and a volley must not dominate. The per-frame gate is
  // what actually handles that — a whole volley in one frame makes ONE sound —
  // so the volume was paying twice for the same problem and just left the
  // trails sounding thin.
  //
  // Re-solve, do not nudge, if the file is replaced.
  // trail_sound: 0.34,
  // The fireball's flight (2026-10-19): -10.39 LUFS against the old -7.45,
  // kept at the old one's played level.
  // trail_sound: 0.477,
  // Batch 2 (2026-10-20): -12.57 LUFS against -10.39, kept at the old played level.
  trail_sound: 0.613,
  // hold_and_win_trail (2026-10-22): trail_sound's own file, 3 dB under the
  // chest collect it carries into (-19.81 dB): "too loud, louder than chest
  // collect".
  // hold_and_win_trail: 0.434,
  // The deeper file (-13.33 LUFS) at the same played level (-19.82 dB).
  // hold_and_win_trail: 0.474,
  // hold_and_win_trail: 0.563, // a little louder (2026-10-22): +1.5 dB
  // The base game's fireball trail itself now (2026-10-28: "use same one we
  // use for fireballs in base game … 75% of its original volume") — the file
  // is trail_sound's copy, at 0.75 x trail_sound (0.613).
  // hold_and_win_trail: 0.46,
  // hold_and_win_trail: 0.867, // +5.5 dB (H&W music review, 2026-10-29)
  hold_and_win_trail: 0.729, // "a bit too much" (2026-10-29): -1.5 dB
  // ── The Golden Mystery's four cell beats are ONE level ──
  //
  // upgrade, coin_land, collect_land and collect_activate are all solved onto
  // a shared target of -16.816 dB with gain = 10^((target - fileLUFS)/20), so
  // they play at the same loudness to within 0.01 dB. The files are mastered
  // over 2 dB apart (-7.7 to -9.9), so equal NUMBERS would not have been equal
  // loudness — collect_activate is the hottest file and takes the lowest gain.
  //
  // They are the things that happen TO a cell: it arrives, it is upgraded, it
  // is gathered. The feature fires them in volleys across a board, so a step
  // between them reads as some cells mattering more than others.
  //
  // Only golden_mystery_spin sits apart, deliberately below them: it is the
  // bed they land out of, not one of them.
  //
  // Re-solve, do not nudge, if any of the four files is replaced.
  upgrade: 0.446,
  // -9.9 LUFS. The landings are the feature — they are what the player is
  // watching — so they sit ABOVE the track they land out of, not under it.
  // coin_land: 0.45,
  // The coin's fireball landing (2026-10-19): -8.06 LUFS against the old
  // -9.88, kept on the board level (-16.82 dB).
  // coin_land: 0.365,
  // Batch 3 (2026-10-22): fireball_land, -8.96 LUFS against -8.06, kept at the old played level.
  // coin_land: 0.405,
  // Under the chests, scarab and jackpot coins it lands among (2026-10-22,
  // "fireball land is louder than chest land and scarab land"): -3 dB.
  // coin_land: 0.287,
  // coin_land: 0.241, // "loud in general, silence it a little bit" (2026-10-22): -1.5 dB
  // The BASE GAME's fireball landing, level with scatter_land and wild_land
  // (-16.81 dB played) — he: "scatter, wild and fireball equal" (2026-10-22).
  coin_land: 0.405,
  // ...and Hold & Win's copy, at the lower level he set there (-21.32 dB).
  // hold_and_win_coin_land: 0.241,
  // hold_and_win_coin_land: 0.287, // "too quiet, louder a bit" (2026-10-28): +1.5 dB
  hold_and_win_coin_land: 0.541, // +5.5 dB (H&W music review, 2026-10-29)
  // -9.0 LUFS, 2.4s. On the same solved level. See `upgrade`.
  collect_land: 0.406,
  // -7.7 LUFS, the hottest of the group, so the lowest gain. See `upgrade`.
  collect_activate: 0.351,
  // -8.4 LUFS. On the same solved level as the other landings. See `upgrade`.
  multiplier_appear: 0.377,
  // ── Batch 3 (2026-10-22): the Hold & Win board, all on the board level
  // (-16.81 dB) the landings sit on ──
  // Jackpot coins land in place of coin_land: -9.25 to -9.61 LUFS.
  mini_coin: 0.43,
  minor_coin: 0.419,
  midi_coin: 0.435,
  major_coin: 0.43,
  grand_coin: 0.437,
  // The H&W copies: +5.5 dB (H&W music review, 2026-10-29).
  hold_and_win_mini_coin: 0.811,
  hold_and_win_minor_coin: 0.79,
  hold_and_win_midi_coin: 0.82,
  hold_and_win_major_coin: 0.811,
  hold_and_win_grand_coin: 0.824,
  // Chests: land -7.83 / -6.96, collect -9.09 / -9.90 LUFS.
  // silver_chest_land: 0.356,
  silver_chest_land: 0.71, // +6.0 dB (H&W music review, 2026-10-29)
  // gold_chest_land: 0.322,
  gold_chest_land: 0.642, // +6.0 dB (H&W music review, 2026-10-29)
  // silver_chest_collect: 0.411,
  silver_chest_collect: 0.82, // +6.0 dB (H&W music review, 2026-10-29)
  // gold_chest_collect: 0.451,
  gold_chest_collect: 0.85, // +5.5 dB (H&W music review, 2026-10-29)
  // Scarab: land -7.00, activate -7.07 LUFS.
  // scarab_land: 0.323,
  scarab_land: 0.644, // +6.0 dB (H&W music review, 2026-10-29)
  // scarab_activate: 0.326,
  // The designer's new multiply sound (2026-10-28, delivered as "Scatter v2"
  // by mistake): at the old one's played level (-18.3 dB from the sprite).
  // scarab_activate: 0.353,
  scarab_activate: 0.665, // +5.5 dB (H&W music review, 2026-10-29)
  // The row unlock: -8.89 LUFS.
  // unlock: 0.402,
  // Shortened (2026-10-22, the designer's 0.76 s): sprite loudest -15.8 against
  // the old -10.6, kept at the old played level (-18.5 dB): 0.402 x 10^(5.2 / 20).
  // unlock: 0.731,
  unlock: 1.0, // +5.5 (file +4.5 dB baked, limited -1 dBTP) (H&W music review, 2026-10-29)
  // ── The three WIN CHIMES are one level: -8.842 dB ──
  //
  // basic_win, scatter_win and golden_mystery_win are the same statement in
  // three situations — an ordinary win paid, the trigger paid, the feature
  // paid — and only ever ONE of them plays in a round. So a step between them
  // means the round sounds bigger or smaller depending on which KIND of win it
  // was, which is not a thing the player should be told.
  //
  // The files span 3.4 dB (-5.3 to -8.7), so the gains span 0.595 to 0.878 —
  // equal numbers here would be nowhere near equal loudness.
  //
  // Nudged down 1 dB from -8.842, all three together. At the old level the
  // chime burst out of the round: it is meant to be the loudest thing on a
  // paid spin, but it was 8.0 dB over everything the symbols do and 1.7 dB
  // over the win banners. It still leads — 6.0 dB over the board, 0.3 dB over
  // the banners — without the jump.
  //
  // Re-solve, do not nudge, if any of the three files is replaced.
  // ── THE WIN CHIMES CAME DOWN 7 dB, 2026-09-22 ──
  //
  // All four were solved onto -9.81 dB and were 7 dB above everything a symbol
  // does — the doors, the landings, the collector, which all sit at -16.816 dB.
  // Vladimir: "the basic win sound is really loud, can we set it to be loud as
  // the doors opening or landing, it has a boomy sound so its kinda annoying
  // when its this loud."
  //
  // So they are re-solved onto that same -16.816 dB. They stay level with each
  // other, which is the point of them being a family: they are SUBSTITUTES, not
  // escalations — scatter_win replaces basic_win on a trigger, golden_mystery_win
  // replaces it when the feature paid, scatter_lose is the blank's answer to
  // scatter_win. Moving one alone would make a trigger 7 dB louder than an
  // ordinary win for no reason the player could explain.
  //
  // Re-solve, do not nudge, if any file is replaced.
  golden_mystery_win: 0.393,
  // -10.5 LUFS, the quietest of the landing group, so the highest gain. Solved
  // onto the SAME -16.816 dB as the golden cells' landings: a scatter arriving
  // and a coin arriving are the same kind of statement, and a board that drops
  // both should not rank them.
  //
  // It still stands IN PLACE OF reel_stop rather than beside it — that is the
  // resolver's job, not the mixer's. See _playLandingSound.
  // scatter_land: 0.482,
  // New file (2026-10-19): -7.83 LUFS against the old -10.47, kept on the
  // board level (-16.81 dB).
  // scatter_land: 0.356,
  // Batch 2 (2026-10-20): -8.99 LUFS against -7.83, kept at the old played level.
  // scatter_land: 0.407,
  // Batch 3 (2026-10-22): the OLD fireball landing (coin_land until batch 3)
  // is the scatter's landing now — -8.06 LUFS, at its own old played level,
  // which is the board level scatter_land sat on too (-16.81 dB).
  scatter_land: 0.365,
  // The wild's landing (2026-10-19): -6.22 LUFS, solved onto the same board
  // level as scatter_land and coin_land (-16.81 dB).
  // wild_land: 0.295,
  // Batch 2 (2026-10-20): -8.84 LUFS against -6.22, kept at the old played level.
  wild_land: 0.399,
  // ── The win line tick: solved onto the landing group, then taken DOWN ──
  //
  // -9.20 LUFS. Levelled with the landings above would be 0.416, playing at
  // -16.818 dB. Taken down twice by ear, 2026-09-17 — 20% and then a further
  // 5%:
  //
  //   0.416 x 0.8 x 0.95 = 0.316  ->  plays at -19.206 dB
  //
  // which is 0.760 of the solved level, 2.39 dB under the group.
  //
  // It earns the drop by being the most REPEATED sound in the game. Every other
  // member of that group fires once for one event; this one fires once per line
  // and a cycle can step through a dozen, then cycle again. At the group's own
  // level it stopped pointing at the win and started competing with it.
  //
  // Still solved, not chosen: the 0.8 is a ratio against a measured level, so a
  // replacement file means re-solving 0.416 from its new LUFS and applying the
  // same 0.760. Do not nudge 0.316 directly.
  // win_line: 0.316,
  // Batch 2 (2026-10-20): their new file, -13.72 LUFS against -9.20, kept at
  // the old played level. ONCE per line (the half-way second play is off).
  // win_line: 0.532,
  win_line: 0.447, // a tiny bit quieter (2026-10-20): -1.5 dB
  // ── The mystery chain is ONE level: drop, build-up, doors ──
  //
  // The four files are within 0.5 dB of each other on disk (-8.0 to -8.5), so
  // they are a matched set and one number very nearly levels them. These are
  // the three beats of a single event and the player should hear them as that,
  // not as three sizes.
  //
  // ── The mystery chain sits with the LANDINGS, at -16.816 dB ──
  //
  // Drop, both build-ups, doors and the golden reveal — all solved with
  // gain = 10^((target - fileLUFS)/20) against the same target as coin_land,
  // scatter_land, collect_land, upgrade and the trails. One level for
  // everything that happens ON the board.
  //
  // It briefly sat 3.9 dB above that, at -12.897, which came from chasing the
  // doors: they were inaudible at 0.4 and the whole chain was raised to rescue
  // them. That fixed the door and made the mystery the loudest thing in the
  // game. The doors being buried was a MASKING problem — a 3.0s build-up
  // ringing under a 1.1s door — and volume was the wrong tool for it.
  //
  // scatter_land is the reference: Vladimir judged that one correct, and a
  // mystery landing and a scatter landing are the same kind of event.
  //
  // Re-solve, do not nudge, if any of these files is replaced.
  //
  // -8.0 LUFS, the hottest of the five, so the lowest gain.
  // ── 3 dB UNDER the landing group, on purpose ──
  //
  // Levelled with scatter_land it still read as louder, and the reason is not
  // the file: `mystery_land` fires ONCE PER REEL. A Golden Mystery covers four
  // full reels, so it plays four times across the stagger; a scatter board
  // plays scatter_land two or three times. Same loudness per hit, more hits.
  //
  // playOncePerFrame only dedupes within a frame, and the reels land about
  // 200ms apart, so that is working as designed — the per-reel announcement is
  // the point. The answer is the level, not the gate.
  //
  // Vladimir, 2026-09-22: "its still too loud make it a bit quieter it doesnt
  // seem to have same volume as scatter." Down 3 dB from 0.362.
  // x0.708 per further -3 dB.
  mystery_land: 0.256,
  // -8.5 LUFS. On the shared level. If the doors read as buried again, the fix
  // is a `cutSound` on symbolData[9].reveal so the build-up ducks out as they
  // open — NOT a bigger number here, which is what put this whole chain 3.9 dB
  // too high once already.
  // ── SOLVED ONTO scatter_land, 2026-09-22 ──
  //
  // Vladimir: "make them same loudness as when scatter drops." So both doors
  // take scatter_land's own effective level, -16.84 dB, exactly:
  //
  //   scatter_land   -10.5 LUFS x 0.482 = -16.84 dB   the reference
  //   door_opening    -8.4 LUFS x 0.379 = -16.84 dB
  //   scatter_door    -8.7 LUFS x 0.392 = -16.84 dB
  //
  // HONEST NOTE: this is within 0.1 dB of where they already were (0.382 and
  // 0.395), so the number was never the thing that made them read as loud.
  // They are the boomiest files in the set and K-weighting discounts the low
  // end that carries that. If the ear still says loud at this level, the fix is
  // the FILE, not the gain — the alternative is to take them below the
  // reference, which is not what was asked for.
  //
  // They are also not stacking: both go through playOncePerFrame, and the
  // Golden Mystery's 24 lids share that gate, so a full board is one door.
  door_opening: 0.378,
  // Both -8.3 LUFS, so one number keeps the two build-ups level with each other
  // and with the landing that precedes them. The DRAMA is the clip length —
  // 1.5s against 3.0s — not the volume.
  mystery_reveal_fast: 0.375,
  mystery_reveal_slow: 0.374,
  // -9.0 LUFS, the quietest of the five, so the highest gain. The gold sign
  // burning away arrives at exactly the level the doors that uncovered it did
  // — consecutive beats of one reveal must not step.
  golden_mystery_reveal: 0.407,
  // -8.7 LUFS. On the shared board level with everything else a symbol does —
  // it is the gamble scatter's door, and door_opening is the mystery's. One
  // gesture, one level, whichever symbol is making it.
  scatter_door: 0.392,
  // -5.3 LUFS, the hottest file in the game and it clips — so the lowest gain
  // of the three despite being the loudest on disk. Solved onto basic_win.
  scatter_win: 0.266,
  // ── The blank, levelled ONTO scatter_win — same beat, two answers ──
  //
  // -6.97 LUFS, so 10^((-16.816 + 6.97)/20) = 0.322, which plays at -16.82 dB
  // against scatter_win's -9.810. Two thousandths of a dB apart.
  //
  // They must match, and more strictly than most pairs: these two answer the
  // SAME moment — the gold scatter's strip stopping — and the player never
  // hears them together, only one or the other. A difference between them
  // would read as the win being the bigger event, which is true of the outcome
  // and must not be true of the mix: the player has to learn the answer from
  // WHICH sound played, not from how loud it was.
  //
  // Re-solve both together if either file is replaced.
  scatter_lose: 0.322,
  // -7.4 LUFS, 2.0s. The room announcing itself, so it sits with the banners
  // rather than with the UI.
  // free_spin_intro: 0.5,
  // Batch 2 (2026-10-20): -10.37 LUFS against -7.40, kept at the old played level.
  free_spin_intro: 0.704,
  // -9.6 LUFS, 1.1s. Levelling it with the announcements (free_spin_intro,
  // total_win) would put it at 0.65; held a shade under, because the wipe is
  // the connective beat between two rooms rather than an announcement of its
  // own. It also plays into a gap: the outgoing track fades as the wipe STARTS
  // and the incoming one only fades in when it COMPLETES.
  // transition: 0.6,
  // Batch 2 (2026-10-20): -10.55 LUFS against -9.63, kept at the old played level.
  transition: 0.667,
  // -9.7 LUFS, 2.3 dB quieter than the open, so the higher number lands the
  // pair at the same level. They are one bookend and should match.
  free_spin_intro_close: 0.6,
  // ── The three ways the room can END are ONE level ──
  //
  // Solved onto a shared target of -13.427 dB, which is where total_win
  // already sat next to free_spin_intro — the room announcing itself and the
  // room saying how it went should arrive at the same size, whichever of the
  // three endings it is. The files are mastered 2.8 dB apart (-6.2 to -9.0),
  // so equal numbers would not be equal loudness.
  //
  // Each plays into a ducked feature track, so none is competing with music.
  //
  // Re-solve, do not nudge, if any of the three files is replaced.
  // total_win: 0.6,
  // Batch 3 (2026-10-22): total_win_sound is -19.49 LUFS — 6 dB under the old
  // one's played level (-13.43 dB) even at 1.0. +7.5 dB baked into the file
  // (limited to -0.5 dBTP): -13.53 LUFS, so 1.0 plays it at the old level.
  total_win: 1.0,
  // max_win: 0.434,
  // Batch 3 (2026-10-22): their "grand_win_banner" IS the max win, -8.55 LUFS
  // against -6.18, kept at the old played level (-13.43 dB).
  max_win: 0.57,
  // royal_win — NEW (2026-10-22): -9.35 LUFS, on max_win's played level — the
  // stand-in it replaces.
  royal_win: 0.625,
  no_win: 0.457,

  // The three Additional Free Spins beats. The files are mastered 7 dB apart
  // (-5.4 / -7.0 / -12.1), so these numbers exist to bring them level with each
  // other and with the free-spins intro pair — the shape is in the clips.
  additional_free_spins_intro: 0.4,
  additional_free_spins_win: 0.5,
  additional_free_spins_outro: 0.8,

  // -8.7 and -8.9 LUFS. The add fires up to four times a charge, so it is held
  // below the close, which happens once and is the thing being built up to.
  level_progress_add: 0.35,
  level_progress_close: 0.5,

  // The gamble four. `spin` is a 6.7s bed under a 4.5s spin, so it sits with
  // the other spin loops rather than with the announcements; `loss` is held
  // down because a loss should not be the loudest thing in the feature.
  gamble_intro: 0.4,
  // -10.8 LUFS. ONE of the three spin beds — down 2.36 dB from 0.45 onto
  // golden_mystery_spin's -20.10 dB. See the note at scatter_spin, which
  // carries the arithmetic for all three.
  gamble_spin: 0.343,
  gamble_loss: 0.3,
  // -6.2 LUFS, levelled against the OUTRO (-7.3 at 0.5) rather than against
  // the loss. That puts it ~2 dB above gamble_loss, which is the point: the
  // rat and the package are the two outcomes of the same spin, and the one
  // the player wanted must not be the quieter of the pair.
  gamble_win: 0.45,
  gamble_outro: 0.5,

  // ── UI ──
  // button_click: 0.3,
  // Batch 2 (2026-10-20): -10.32 LUFS against -9.58, kept at the old played level.
  // button_click: 0.327,
  // "really loud" (2026-10-20): matched to the OLD click, which was loud
  // itself — and it now plays for every tap-to-continue too. -6 dB (half).
  button_click: 0.164,
  // tap_to_continue: 0.7, (unused — tap_to_continue resolves to button_click)
  // ── The laugh belongs with the BANNERS, not with the UI ──
  //
  // -6.8 LUFS, which is within 0.15 dB of big_win on disk. Solved onto the
  // banner target of -11.127 dB, so the celebration closes at the size it
  // opened.
  //
  // It was 0.35, levelled against tap_to_continue on the reasoning that it was
  // the sound a tap makes. That was the wrong family: it is the last beat of
  // the win celebration, and at 0.35 it came in 4.8 dB under the banner it was
  // closing — the celebration shrinking as it ended.
  //
  // Two screens use it: the win celebration's own close and the free-spins
  // TOTAL WIN popup, which closes the same way for the same reason.
  //
  // Re-solve, do not nudge, if the file is replaced.
  // win_animation_end: 0.61,
  // ── Replaced (2026-10-22, big_wins_end_sound — this game's own shot) ──
  // its stereo halves are uncorrelated, so the mono sprite lost 7 dB: the
  // source is mixed to mono here (+6 dB, limited at -1 dBTP). Solved on the
  // sprite's loudest moment: old -9.0 x 0.61 = -13.3 dB played, new -12.8.
  win_animation_end: 0.945,
  // Total Win's copy: "too loud, tune it down a bit" (2026-10-22) — -3 dB.
  total_win_end: 0.669,
  // collector_last: sprite loudest -18.2 — at full volume 2 dB over the gold
  // chest collect (-13.3 x 0.451 = -20.2 dB): "a bit louder" than the regular
  // hits to come.
  // collector_last: 1,
  // A CHINESE GONG now (2026-10-29, review: "played as a final hit of
  // collector instead of the current one"): the variant at 11.05 s of
  // "Classic Chinese Gong Sound Effect", cut at its strike, 7 s with its ring
  // faded over the last 3 s, peak -1 dBFS; nearly mono (L/R 0.97). At the old
  // hit's played level — sprite loudest -13.2 dB, so -18.2 dB played.
  // collector_last: 0.562,
  collector_last: 0.989, // +6.5 (file +2 dB baked, limited -1 dBTP) (H&W music review, 2026-10-29)
  // collector_hit: -25.01 LUFS, mixed to mono +8 dB in the file (peak -1.7):
  // sprite loudest -20.2 = the gold chest collect, 2 dB under collector_last.
  // collector_hit: 1,
  // collector_hit: 1.0, // +6.0 (file +8.5 dB baked, limited -1 dBTP) (H&W music review, 2026-10-29)
  collector_hit: 0.841, // "turn them down a bit" (2026-10-29): -1.5 dB
  // spin: 0.3,
  // Batch 2 (2026-10-20): -9.95 LUFS against -8.96, kept at the old played level.
  // spin: 0.336,
  // Down with button_click (2026-10-20): the same loud old reference, -6 dB.
  // spin: 0.168,
  // Back to its matched level (2026-10-22: "can't hear it at all") — at 0.168
  // it played under the reel spin that starts on the same frame (-25.4 vs
  // -24.5 dB). The click stays down; it plays alone.
  spin: 0.336,
  // basic_win: 0.338,
  // Batch 2 (2026-10-20): -8.09 LUFS against -7.43, kept at the old played level.
  // basic_win: 0.365,
  // Batch 3 (2026-10-22): win_small, -7.15 LUFS against -8.09, kept at the old played level.
  // basic_win: 0.328,
  // basic_win: 0.39, // a little louder (2026-10-22): +1.5 dB
  basic_win: 0.463, // a bit more (2026-10-22): +1.5 dB
  // win_ (2026-10-28): level with basic_win as PLAYED from the mono sprite
  // (-9.7 dB loudest 400 ms) — its stereo loses 1.8 dB more in the fold.
  // win_: 0.57,
  // ...and ABOVE it (2026-10-28: "make it louder, that one will be more
  // important than basic win"): +3 dB.
  // win_: 0.805,
  win_scatter: 0.805,
  // "win 3" (2026-10-28), the Hold & Win trigger: the same played level as
  // win_scatter (-13.4 dB from the sprite, 3 dB over basic_win).
  win_coin: 0.863,
};
